<template>
  <div class="container data-board-page">
    <div class="title">
      <el-breadcrumb>
        <el-breadcrumb-item>数据看板</el-breadcrumb-item>
        <el-breadcrumb-item>数据运营</el-breadcrumb-item>
        <el-breadcrumb-item>Dcops工单运营</el-breadcrumb-item>
        <el-breadcrumb-item>服务器搬迁</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <el-card class="box-card">
      <div class="search-title">
        <i class="el-icon-search"></i>
        搜索条件
      </div>
      <div class="search-hint">提示：先选择周期类型，再选择当前周期和对比周期。</div>
      <div class="filter-row">
        <div class="filter-block filter-box">
          <div class="filter-label">园区范围</div>
          <el-select v-model="query.campus" placeholder="全部园区（可选）" clearable class="filter-item"
            @change="loadBoardData">
            <el-option label="全部" value="" />
            <el-option v-for="item in campusOptions" :key="item" :label="item" :value="item" />
          </el-select>
        </div>
        <div class="filter-block filter-box">
          <div class="filter-label">周期类型</div>
          <el-select v-model="query.period" placeholder="请选择周期类型" class="filter-item" @change="handlePeriodTypeChange">
            <el-option v-for="item in periodOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </div>
        <div class="period-compare-box">
          <div class="period-compare-header">
            <span class="current-dot"></span>
            <span>当前周期</span>
            <span class="vs-text">VS</span>
            <span class="compare-dot"></span>
            <span>对比周期</span>
          </div>
          <div class="period-compare-row">
            <el-select
              v-model="query.currentPeriodKey"
              placeholder="当前周期"
              class="period-item"
              @change="handleCurrentPeriodChange"
            >
              <el-option v-for="item in periodSelectionOptions" :key="`c-${item.key}`" :label="item.label"
                :value="item.key" />
            </el-select>
            <el-select v-model="query.comparePeriodKey" placeholder="对比周期" class="period-item" @change="loadBoardData">
              <el-option v-for="item in periodSelectionOptions" :key="`p-${item.key}`" :label="item.label"
                :value="item.key" />
            </el-select>
          </div>
        </div>
        <div class="filter-block button-block">
          <div class="filter-label">执行操作</div>
          <el-button type="primary" :loading="loading" @click="loadBoardData">查询</el-button>
        </div>
        <div class="alert-highlight" @click="openAlertDrawer">
          <div class="alert-title">
            <i class="el-icon-bell"></i>
            告警工单
          </div>
          <div class="alert-value">{{ overviewCurrent.alert_orders || 0 }}</div>
          <div class="alert-desc">进行中且已超时</div>
        </div>
      </div>
      <div class="time-range" v-if="filterInfo.current_start">
        当前周期：{{ formatDateRange(filterInfo.current_start, filterInfo.current_end) }}，
        对比周期：{{ formatDateRange(filterInfo.previous_start, filterInfo.previous_end) }}
      </div>
    </el-card>

    <el-row :gutter="16" class="mt-20">
      <el-col :span="4" v-for="item in kpiCards" :key="item.key">
        <el-card class="kpi-card" :class="{ clickable: isKpiClickable(item.key) }" @click.native="handleKpiClick(item)">
          <div class="kpi-title">
            <i :class="item.icon" class="kpi-icon"></i>
            {{ item.title }}
          </div>
          <div class="kpi-value" :class="[item.valueClass, { compact: item.key === 'overtime_orders' }]">
            {{ getKpiDisplayValue(item) }}
          </div>
          <div class="kpi-compare">
            <span :class="getCompareClass(getCompareValue(item))">
              {{ formatCompareText(getCompareValue(item)) }}
            </span>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="mt-20">
      <el-col :span="12">
        <el-card class="box-card">
          <div class="card-title">近12个月工单数量趋势</div>
          <div ref="monthlyTrendChartRef" class="chart-box" />
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card class="box-card">
          <div class="card-title">今年园区工单占比</div>
          <div ref="campusShareChartRef" class="chart-box" />
        </el-card>
      </el-col>
    </el-row>

    <el-drawer :visible.sync="alertDrawerVisible" size="60%" direction="rtl">
      <div slot="title" class="drawer-header">
        <span>告警工单（进行中且已超时）</span>
        <el-button size="mini" type="primary" plain @click="exportAlertTickets">导出Excel</el-button>
      </div>
      <div class="alert-drawer-body">
        <el-table :data="alertTicketList" border stripe>
          <el-table-column prop="TicketId" label="工单号" min-width="140" />
          <el-table-column prop="Campus" label="园区" min-width="120" />
          <el-table-column prop="Scene" label="场景" width="80" />
          <el-table-column label="超时工单涉及人员" min-width="260">
            <template slot-scope="scope">
              <div class="staff-tags" v-if="scope.row.involved_staff_list && scope.row.involved_staff_list.length">
                <el-tag v-for="name in scope.row.involved_staff_list" :key="name" size="mini" type="info"
                  effect="plain">
                  {{ name }}
                </el-tag>
              </div>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="overtime_hours" label="超时小时" width="90" />
          <el-table-column prop="CreateTime" label="创建时间" width="170" />
          <el-table-column prop="OlaTime" label="OLA时间" width="170" />
          <el-table-column prop="ProcessTitle" label="工单标题" min-width="220" show-overflow-tooltip />
          <el-table-column label="操作" width="90" fixed="right" align="center">
            <template slot-scope="scope">
              <el-button
                type="danger"
                size="mini"
                :loading="dismissLoadingId === scope.row.TicketId"
                @click="handleDismissAlert(scope.row)"
              >
                结单
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination-wrap">
          <el-pagination background layout="total, prev, pager, next, sizes" :current-page="alertPagination.page"
            :page-size="alertPagination.size" :page-sizes="[20, 50, 100]" :total="alertPagination.total"
            @size-change="handleAlertSizeChange" @current-change="handleAlertPageChange" />
        </div>
      </div>
    </el-drawer>

    <el-drawer :visible.sync="overtimeDrawerVisible" size="60%" direction="rtl">
      <div slot="title" class="drawer-header">
        <span>超时工单（超时率/超时工单数量）</span>
        <el-button size="mini" type="primary" plain @click="exportOvertimeTickets">导出Excel</el-button>
      </div>
      <div class="alert-drawer-body">
        <el-table :data="overtimeTicketList" border stripe>
          <el-table-column prop="TicketId" label="工单号" min-width="140" />
          <el-table-column prop="Campus" label="园区" min-width="120" />
          <el-table-column prop="Scene" label="场景" width="80" />
          <el-table-column prop="InstanceStatus" label="状态" width="90" />
          <el-table-column label="超时工单涉及人员" min-width="260">
            <template slot-scope="scope">
              <div class="staff-tags" v-if="scope.row.involved_staff_list && scope.row.involved_staff_list.length">
                <el-tag v-for="name in scope.row.involved_staff_list" :key="name" size="mini" type="info"
                  effect="plain">
                  {{ name }}
                </el-tag>
              </div>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="overtime_hours" label="超时小时" width="90" />
          <el-table-column prop="CreateTime" label="创建时间" width="170" />
          <el-table-column prop="EndTime" label="结束时间" width="170" />
          <el-table-column prop="OlaTime" label="OLA时间" width="170" />
          <el-table-column prop="ProcessTitle" label="工单标题" min-width="220" show-overflow-tooltip />
        </el-table>
        <div class="pagination-wrap">
          <el-pagination background layout="total, prev, pager, next, sizes" :current-page="overtimePagination.page"
            :page-size="overtimePagination.size" :page-sizes="[20, 50, 100]" :total="overtimePagination.total"
            @size-change="handleOvertimeSizeChange" @current-change="handleOvertimePageChange" />
        </div>
      </div>
    </el-drawer>

    <el-drawer :visible.sync="kpiDrawerVisible" size="60%" direction="rtl" :destroy-on-close="true">
      <div slot="title" class="drawer-header">
        <span>{{ kpiDrawerTitle }}</span>
        <el-button size="mini" type="primary" plain @click="exportKpiDetailTickets">导出Excel</el-button>
      </div>
      <div class="alert-drawer-body">
        <el-table :data="kpiTicketList" border stripe>
          <el-table-column prop="TicketId" label="工单号" min-width="140" />
          <el-table-column prop="Campus" label="园区" min-width="120" />
          <el-table-column prop="Scene" label="场景" width="80" />
          <el-table-column prop="InstanceStatus" label="状态" width="110" />
          <el-table-column prop="CreateTime" label="创建时间" width="170" />
          <el-table-column prop="OlaTime" label="OLA时间" width="170" />
          <el-table-column prop="EndTime" label="结束时间" width="170" />
          <el-table-column prop="ProcessTitle" label="工单标题" min-width="220" show-overflow-tooltip />
          <el-table-column label="涉及人员" min-width="260">
            <template slot-scope="scope">
              <div
                class="staff-tags"
                v-if="scope.row.involved_staff_list && scope.row.involved_staff_list.length"
              >
                <el-tag
                  v-for="name in scope.row.involved_staff_list"
                  :key="name"
                  size="mini"
                  type="info"
                  effect="plain"
                >
                  {{ name }}
                </el-tag>
              </div>
              <span v-else>-</span>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination-wrap">
          <el-pagination
            background
            layout="total, prev, pager, next, sizes"
            :current-page="kpiPagination.page"
            :page-size="kpiPagination.size"
            :page-sizes="[20, 50, 100]"
            :total="kpiPagination.total"
            @size-change="handleKpiSizeChange"
            @current-change="handleKpiPageChange"
          />
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { dismissDcopsMoveAlertTicket, getDcopsMoveAlertTickets, getDcopsMoveBoardStatistics, getDcopsMoveCampusOptions, getDcopsMoveOvertimeTickets } from '@/api/analysis';
import { getServerMoveList } from '@/api/dcopsTicket';
import dayjs from 'dayjs';
import * as echarts from 'echarts';
import * as XLSX from 'xlsx';

export default {
  name: 'DcopsMoveBoard',
  data() {
    return {
      loading: false,
      periodOptions: [
        { label: '周', value: 'week' },
        { label: '月', value: 'month' },
        { label: '季度', value: 'quarter' },
        { label: '上/下半年', value: 'half_year' },
        { label: '年', value: 'year' },
      ],
      query: {
        campus: '',
        period: 'week',
        currentPeriodKey: '',
        comparePeriodKey: '',
      },
      periodSelectionOptions: [],
      campusOptions: [],
      filterInfo: {},
      overviewCurrent: {},
      overviewCompare: {},
      monthlyTrend12m: [],
      campusShareThisYear: [],
      monthlyTrendChart: null,
      campusShareChart: null,
      alertDrawerVisible: false,
      alertTicketList: [],
      dismissLoadingId: '',
      alertPagination: {
        page: 1,
        size: 20,
        total: 0,
      },
      overtimeDrawerVisible: false,
      overtimeTicketList: [],
      overtimePagination: {
        page: 1,
        size: 20,
        total: 0,
      },
      kpiDrawerVisible: false,
      kpiDrawerTitle: '',
      kpiDetailKey: '',
      kpiTicketList: [],
      kpiPagination: {
        page: 1,
        size: 20,
        total: 0,
      },
      kpiCards: [
        { title: '工单总数', key: 'total_orders', compareKey: 'total_orders_change_pct', icon: 'el-icon-tickets', valueClass: 'main' },
        { title: '完成工单', key: 'completed_orders', compareKey: 'completed_orders_change_pct', icon: 'el-icon-circle-check', valueClass: 'success' },
        { title: '超时率/超时工单数量', key: 'overtime_orders', compareKey: 'overtime_rate_change_pct', icon: 'el-icon-warning-outline', valueClass: 'danger' },
        { title: '进行中工单', key: 'in_progress_orders', compareKey: '', icon: 'el-icon-time', valueClass: 'warning' },
        { title: '预超时工单', key: 'pre_overtime_orders', compareKey: '', icon: 'el-icon-alarm-clock', valueClass: 'warning' },
        { title: '驳回工单', key: 'rejected_orders', compareKey: '', icon: 'el-icon-close-notification', valueClass: 'muted' }
      ],
    };
  },
  computed: {
    darkMode() {
      return this.$store.state.setting.darkMode;
    },
  },
  watch: {
    darkMode() {
      this.$nextTick(() => this.renderCharts());
    },
  },
  async created() {
    this.initPeriodSelection();
    await this.loadCampusOptions();
    await this.loadBoardData();
  },
  mounted() {
    this.monthlyTrendChart = echarts.init(this.$refs.monthlyTrendChartRef);
    this.campusShareChart = echarts.init(this.$refs.campusShareChartRef);
    this.renderCharts();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.monthlyTrendChart) this.monthlyTrendChart.dispose();
    if (this.campusShareChart) this.campusShareChart.dispose();
  },
  methods: {
    getPayloadData(res) {
      return (res && res.data && res.data.data) || {};
    },
    async loadCampusOptions() {
      try {
        const res = await getDcopsMoveCampusOptions();
        const data = this.getPayloadData(res);
        this.campusOptions = data.campus_list || [];
      } catch (error) {
        console.error('获取园区选项失败:', error);
      }
    },
    async loadBoardData() {
      this.loading = true;
      try {
        const selectedCurrent = this.periodSelectionOptions.find((item) => item.key === this.query.currentPeriodKey);
        const selectedCompare = this.periodSelectionOptions.find((item) => item.key === this.query.comparePeriodKey);
        const res = await getDcopsMoveBoardStatistics({
          campus: this.query.campus,
          period: this.query.period,
          with_compare: true,
          current_start: selectedCurrent ? selectedCurrent.start : null,
          current_end: selectedCurrent ? selectedCurrent.end : null,
          compare_start: selectedCompare ? selectedCompare.start : null,
          compare_end: selectedCompare ? selectedCompare.end : null,
          current_label: selectedCurrent ? selectedCurrent.label : '',
          compare_label: selectedCompare ? selectedCompare.label : '',
        });
        const data = this.getPayloadData(res);
        this.filterInfo = data.filter_info || {};
        this.overviewCurrent = (data.overview && data.overview.current) || {};
        this.overviewCompare = (data.overview && data.overview.compare) || {};
        this.monthlyTrend12m = data.monthly_trend_12m || [];
        this.campusShareThisYear = data.campus_share_this_year || [];
        this.renderCharts();
      } catch (error) {
        console.error('获取看板数据失败:', error);
      } finally {
        this.loading = false;
      }
    },
    getBoardChartTheme() {
      const dark = this.darkMode;
      return {
        text: dark ? '#c5cad3' : '#4a5872',
        textSecondary: dark ? '#8b939e' : '#6b778c',
        axisLine: dark ? '#3d4654' : '#dce6f8',
        splitLine: dark ? '#343e4c' : '#eef2f8',
        tooltipBg: dark ? '#2f3844' : '#ffffff',
        tooltipBorder: dark ? '#3d4654' : '#e8edf5',
      };
    },
    buildChartTooltip(theme) {
      return {
        backgroundColor: theme.tooltipBg,
        borderColor: theme.tooltipBorder,
        textStyle: { color: theme.text },
      };
    },
    renderCharts() {
      const theme = this.getBoardChartTheme();
      const monthLabels = [];
      for (let i = 11; i >= 0; i -= 1) {
        monthLabels.push(dayjs().subtract(i, 'month').format('YYYY-MM'));
      }
      const monthMap = {};
      (this.monthlyTrend12m || []).forEach((item) => {
        monthMap[item.month] = item;
      });
      const totalSeries = monthLabels.map((month) => (monthMap[month] && monthMap[month].total_orders) || 0);
      const overtimeSeries = monthLabels.map((month) => (monthMap[month] && monthMap[month].overtime_orders) || 0);
      const overtimeRateSeries = monthLabels.map((month, index) => {
        const total = totalSeries[index];
        const overtime = overtimeSeries[index];
        if (!total) return 0;
        return Number(((overtime / total) * 100).toFixed(2));
      });

      if (this.monthlyTrendChart) {
        this.monthlyTrendChart.setOption({
          tooltip: {
            trigger: 'axis',
            ...this.buildChartTooltip(theme),
            formatter: (params) => {
              if (!Array.isArray(params) || !params.length) return '';
              const lines = [params[0].axisValueLabel || params[0].name];
              params.forEach((item) => {
                const isRate = item.seriesName === '超时率';
                const value = isRate ? `${item.value}%` : item.value;
                lines.push(`${item.marker}${item.seriesName}: ${value}`);
              });
              return lines.join('<br/>');
            },
          },
          legend: {
            data: ['总工单', '超时工单', '超时率'],
            textStyle: { color: theme.text },
          },
          grid: { left: '3%', right: '6%', bottom: '3%', containLabel: true },
          xAxis: {
            type: 'category',
            data: monthLabels,
            axisLabel: {
              interval: 0,
              rotate: 30,
              color: theme.text,
            },
            axisLine: { lineStyle: { color: theme.axisLine } },
            axisTick: { lineStyle: { color: theme.axisLine } },
          },
          yAxis: [
            {
              type: 'value',
              name: '工单数',
              nameTextStyle: { color: theme.textSecondary },
              axisLabel: { color: theme.text },
              axisLine: { lineStyle: { color: theme.axisLine } },
              splitLine: { lineStyle: { color: theme.splitLine } },
            },
            {
              type: 'value',
              name: '超时率',
              nameTextStyle: { color: theme.textSecondary },
              min: 0,
              max: 100,
              interval: 20,
              axisLabel: {
                color: theme.textSecondary,
                formatter: '{value}%',
              },
              axisLine: { lineStyle: { color: theme.axisLine } },
              splitLine: { show: false },
            },
          ],
          series: [
            { name: '总工单', type: 'line', smooth: true, data: totalSeries },
            { name: '超时工单', type: 'line', smooth: true, data: overtimeSeries },
            {
              name: '超时率',
              type: 'line',
              smooth: true,
              yAxisIndex: 1,
              data: overtimeRateSeries,
              symbol: 'circle',
              symbolSize: 4,
              lineStyle: {
                width: 1.5,
                type: 'dashed',
                color: theme.textSecondary,
              },
              itemStyle: { color: theme.textSecondary },
              z: 1,
            },
          ],
        });
      }
      if (this.campusShareChart) {
        const pieLabel = {
          color: theme.text,
          fontSize: 11,
          lineHeight: 14,
        };
        const pieData = this.campusShareThisYear.map((item, index) => ({
          name: item.campus,
          value: item.total_orders || 0,
          label: {
            show: index < 10,
            position: 'outside',
            formatter: '{b} - {d}%',
            ...pieLabel,
          },
          labelLine: {
            show: index < 10,
            length: 20,
            length2: 18,
            smooth: true,
            lineStyle: { color: theme.textSecondary },
          },
        }));
        this.campusShareChart.setOption({
          tooltip: {
            trigger: 'item',
            formatter: '{b}<br/>工单数: {c}<br/>占比: {d}%',
            ...this.buildChartTooltip(theme),
          },
          legend: {
            type: 'scroll',
            bottom: 0,
            textStyle: { color: theme.text },
            pageTextStyle: { color: theme.text },
            pageIconColor: theme.textSecondary,
            pageIconInactiveColor: theme.axisLine,
          },
          series: [
            {
              type: 'pie',
              radius: ['35%', '65%'],
              center: ['50%', '45%'],
              data: pieData,
              minAngle: 1,
              avoidLabelOverlap: true,
              label: {
                show: false,
                position: 'outside',
                formatter: '{b} - {d}%',
                ...pieLabel,
              },
              labelLine: {
                show: false,
                length: 20,
                length2: 18,
                smooth: true,
                lineStyle: { color: theme.textSecondary },
              },
              labelLayout: {
                hideOverlap: true,
                moveOverlap: 'shiftY',
                edgeDistance: 18,
              },
            },
          ],
        });
      }
    },
    async loadAlertTickets() {
      const selectedCurrent = this.periodSelectionOptions.find((item) => item.key === this.query.currentPeriodKey);
      const res = await getDcopsMoveAlertTickets({
        campus: this.query.campus,
        period: this.query.period,
        current_start: selectedCurrent ? selectedCurrent.start : null,
        current_end: selectedCurrent ? selectedCurrent.end : null,
        page: this.alertPagination.page,
        size: this.alertPagination.size,
      });
      const data = this.getPayloadData(res);
      this.alertTicketList = data.list || [];
      this.alertPagination.total = data.total || 0;
    },
    async loadOvertimeTickets() {
      const selectedCurrent = this.periodSelectionOptions.find((item) => item.key === this.query.currentPeriodKey);
      const res = await getDcopsMoveOvertimeTickets({
        campus: this.query.campus,
        period: this.query.period,
        current_start: selectedCurrent ? selectedCurrent.start : null,
        current_end: selectedCurrent ? selectedCurrent.end : null,
        page: this.overtimePagination.page,
        size: this.overtimePagination.size,
      });
      const data = this.getPayloadData(res);
      this.overtimeTicketList = data.list || [];
      this.overtimePagination.total = data.total || 0;
    },
    async openAlertDrawer() {
      this.alertDrawerVisible = true;
      this.alertPagination.page = 1;
      await this.loadAlertTickets();
    },
    async handleDismissAlert(row) {
      if (!row || !row.TicketId) return;
      try {
        await this.$confirm(`确认对工单 ${row.TicketId} 结单？结单后将不再参与超时告警。`, '结单确认', {
          type: 'warning',
          confirmButtonText: '结单',
          cancelButtonText: '取消',
        });
      } catch (e) {
        return;
      }
      this.dismissLoadingId = row.TicketId;
      try {
        const res = await dismissDcopsMoveAlertTicket({ ticket_id: row.TicketId });
        const payload = this.getPayloadData(res);
        if (payload && (payload.alarm_dismissed === 1 || payload.msg)) {
          this.$message.success(payload.msg || '结单成功');
          await this.loadAlertTickets();
          await this.loadBoardData();
        } else {
          this.$message.error((res.data && res.data.message) || '结单失败');
        }
      } catch (e) {
        console.error(e);
        this.$message.error('结单失败');
      } finally {
        this.dismissLoadingId = '';
      }
    },
    async openOvertimeDrawer() {
      this.overtimeDrawerVisible = true;
      this.overtimePagination.page = 1;
      await this.loadOvertimeTickets();
    },
    async openKpiDetailDrawer(card) {
      this.kpiDrawerVisible = true;
      this.kpiDrawerTitle = card.title || '工单详情';
      this.kpiDetailKey = card.key;
      this.kpiPagination.page = 1;
      await this.loadKpiDetailTickets();
    },
    async loadKpiDetailTickets() {
      const selectedCurrent = this.periodSelectionOptions.find((item) => item.key === this.query.currentPeriodKey);
      const timeParams = {
        start_time: selectedCurrent ? selectedCurrent.start : null,
        end_time: selectedCurrent ? selectedCurrent.end : null,
      };

      const params = {
        page: this.kpiPagination.page,
        size: this.kpiPagination.size,
        campus: this.query.campus || null,
        ...timeParams,
      };

      if (this.kpiDetailKey === 'completed_orders') params.state = '完成';
      if (this.kpiDetailKey === 'in_progress_orders') params.state = '进行中';
      if (this.kpiDetailKey === 'rejected_orders') params.state = '驳回';
      if (this.kpiDetailKey === 'pre_overtime_orders') params.rd_olaTime = '1';
      if (this.kpiDetailKey === 'total_orders') {
        // total_orders 不加 state / rd_olaTime，保持全量
      }

      const res = await getServerMoveList(params);
      const payload = this.getPayloadData(res);
      const list = payload.data || [];
      this.kpiPagination.total = payload.total || 0;

      this.kpiTicketList = list.map((row) => {
        const involved_staff_list = this.parseInvolvedStaffList(row.TaskList);
        return {
          ...row,
          involved_staff_list,
        };
      });
    },
    async exportKpiDetailTickets() {
      try {
        const selectedCurrent = this.periodSelectionOptions.find((item) => item.key === this.query.currentPeriodKey);
        const timeParams = {
          start_time: selectedCurrent ? selectedCurrent.start : null,
          end_time: selectedCurrent ? selectedCurrent.end : null,
        };

        const params = {
          export_excel: '1',
          page: 1,
          size: 200,
          campus: this.query.campus || null,
          ...timeParams,
        };
        if (this.kpiDetailKey === 'completed_orders') params.state = '完成';
        if (this.kpiDetailKey === 'in_progress_orders') params.state = '进行中';
        if (this.kpiDetailKey === 'rejected_orders') params.state = '驳回';
        if (this.kpiDetailKey === 'pre_overtime_orders') params.rd_olaTime = '1';

        const res = await getServerMoveList(params);
        const payload = this.getPayloadData(res);
        const rows = payload.data || [];

        if (!rows.length) {
          this.$message.warning('暂无可导出数据');
          return;
        }

        const exportRows = rows.map((row) => ({
          工单号: row.TicketId || '',
          园区: row.Campus || '',
          场景: row.Scene || '',
          状态: row.InstanceStatus || '',
          创建时间: row.CreateTime || '',
          OLA时间: row.OlaTime || '',
          结束时间: row.EndTime || '',
          工单标题: row.ProcessTitle || '',
          涉及人员: this.parseInvolvedStaffList(row.TaskList).join('、') || '-',
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'data');
        const ts = dayjs().format('YYYYMMDDHHmm');
        const pageName = '服务器搬迁';
        XLSX.writeFile(workbook, `${pageName}_${this.kpiDrawerTitle}_${ts}.xlsx`);
        this.$message.success('导出成功');
      } catch (error) {
        console.error('导出工单详情失败:', error);
        const detail = error && error.response && error.response.data && error.response.data.detail;
        this.$message.error(detail ? `导出失败：${detail}` : '导出失败，请重试');
      }
    },
    handleKpiPageChange(page) {
      this.kpiPagination.page = page;
      this.loadKpiDetailTickets();
    },
    handleKpiSizeChange(size) {
      this.kpiPagination.size = size;
      this.kpiPagination.page = 1;
      this.loadKpiDetailTickets();
    },
    parseInvolvedStaffList(taskListRaw) {
      if (!taskListRaw) return [];
      let parsed = taskListRaw;
      try {
        if (typeof taskListRaw === 'string') {
          const s = taskListRaw.trim();
          if (!s) return [];
          parsed = JSON.parse(s);
        }
      } catch (e) {
        return [];
      }

      const staffSet = new Set();

      const collectFromDetail = (detail) => {
        if (!detail || typeof detail !== 'object') return;
        const real = detail.RealOperator;
        const op = detail.Operator;
        if (real) staffSet.add(String(real).trim());
        if (op) staffSet.add(String(op).trim());
      };

      if (Array.isArray(parsed)) {
        parsed.forEach((item) => {
          if (!item || typeof item !== 'object') return;
          Object.keys(item).forEach((k) => collectFromDetail(item[k]));
        });
      } else if (parsed && typeof parsed === 'object') {
        ['Updown', 'Line', 'Follow', 'Outin'].forEach((k) => collectFromDetail(parsed[k]));
      }

      return Array.from(staffSet).filter(Boolean).sort();
    },
    async handleAlertPageChange(page) {
      this.alertPagination.page = page;
      await this.loadAlertTickets();
    },
    async handleAlertSizeChange(size) {
      this.alertPagination.size = size;
      this.alertPagination.page = 1;
      await this.loadAlertTickets();
    },
    async handleOvertimePageChange(page) {
      this.overtimePagination.page = page;
      await this.loadOvertimeTickets();
    },
    async handleOvertimeSizeChange(size) {
      this.overtimePagination.size = size;
      this.overtimePagination.page = 1;
      await this.loadOvertimeTickets();
    },
    isKpiClickable(key) {
      return ['total_orders', 'completed_orders', 'overtime_orders', 'in_progress_orders', 'pre_overtime_orders', 'rejected_orders']
        .includes(key);
    },
    handleKpiClick(item) {
      if (item.key === 'overtime_orders') {
        this.openOvertimeDrawer();
        return;
      }
      if (this.isKpiClickable(item.key)) {
        this.openKpiDetailDrawer(item);
      }
    },
    getKpiDisplayValue(item) {
      const value = this.overviewCurrent[item.key] || 0;
      if (item.key !== 'overtime_orders') return value;
      const rate = this.formatRate(this.overviewCurrent.overtime_rate);
      return `${rate} / ${value}`;
    },
    formatRate(value) {
      const num = Number(value);
      if (Number.isNaN(num)) return '0%';
      return `${num}%`;
    },
    async fetchAllTicketPages(type) {
      const selectedCurrent = this.periodSelectionOptions.find((item) => item.key === this.query.currentPeriodKey);
      const apiFn = type === 'alert' ? getDcopsMoveAlertTickets : getDcopsMoveOvertimeTickets;
      // 后端请求模型限制 size <= 200
      const pageSize = 200;
      let page = 1;
      let total = 0;
      const all = [];
      do {
        const res = await apiFn({
          campus: this.query.campus,
          period: this.query.period,
          current_start: selectedCurrent ? selectedCurrent.start : null,
          current_end: selectedCurrent ? selectedCurrent.end : null,
          page,
          size: pageSize,
        });
        const data = this.getPayloadData(res);
        const list = data.list || [];
        total = data.total || 0;
        all.push(...list);
        page += 1;
      } while (all.length < total);
      return all;
    },
    exportRowsToExcel(rows, cardName) {
      if (!rows || !rows.length) {
        this.$message.warning('暂无可导出数据');
        return;
      }
      const exportRows = rows.map((row) => ({
        工单号: row.TicketId || '',
        园区: row.Campus || '',
        场景: row.Scene || '',
        状态: row.InstanceStatus || '',
        超时工单涉及人员: row.involved_staff || ((row.involved_staff_list || []).join('、')) || '-',
        超时小时: row.overtime_hours || 0,
        创建时间: row.CreateTime || '',
        结束时间: row.EndTime || '',
        OLA时间: row.OlaTime || '',
        工单标题: row.ProcessTitle || '',
      }));
      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'data');
      const ts = dayjs().format('YYYYMMDDHHmm');
      const pageName = '服务器搬迁';
      XLSX.writeFile(workbook, `${pageName}_${cardName}_${ts}.xlsx`);
      this.$message.success('导出成功');
    },
    async exportAlertTickets() {
      try {
        const rows = await this.fetchAllTicketPages('alert');
        this.exportRowsToExcel(rows, '告警工单');
      } catch (error) {
        console.error('导出告警工单失败:', error);
        const detail = error && error.response && error.response.data && error.response.data.detail;
        this.$message.error(detail ? `导出失败：${detail}` : '导出失败，请重试');
      }
    },
    async exportOvertimeTickets() {
      try {
        const rows = await this.fetchAllTicketPages('overtime');
        this.exportRowsToExcel(rows, '超时工单');
      } catch (error) {
        console.error('导出超时工单失败:', error);
        const detail = error && error.response && error.response.data && error.response.data.detail;
        this.$message.error(detail ? `导出失败：${detail}` : '导出失败，请重试');
      }
    },
    handleResize() {
      if (this.monthlyTrendChart) this.monthlyTrendChart.resize();
      if (this.campusShareChart) this.campusShareChart.resize();
    },
    getCompareValue(item) {
      if (!item.compareKey) return null;
      return this.overviewCompare[item.compareKey];
    },
    getVsPeriodLabel() {
      return this.filterInfo.previous_label || '上周期';
    },
    formatCompareText(value) {
      if (value === null || value === undefined) return '';
      const vsPeriod = this.getVsPeriodLabel();
      const num = Number(value);
      if (Number.isNaN(num)) return '';
      const arrow = num >= 0 ? '↑' : '↓';
      return `${arrow} ${Math.abs(num)}% vs ${vsPeriod}`;
    },
    formatDateRange(start, end) {
      if (!start || !end) return '--';
      return `${dayjs(start).format('YYYY-MM-DD')} ~ ${dayjs(end).format('YYYY-MM-DD')}`;
    },
    getCompareClass(value) {
      if (value === null || value === undefined) return '';
      return Number(value) >= 0 ? 'up' : 'down';
    },
    getStartOfWeek(input) {
      const day = input.day();
      const mondayOffset = day === 0 ? -6 : 1 - day;
      return input.add(mondayOffset, 'day').startOf('day');
    },
    createRangeOption(period, start, end, label, key) {
      return {
        period,
        start: start.format('YYYY-MM-DD HH:mm:ss'),
        end: end.format('YYYY-MM-DD HH:mm:ss'),
        label,
        key,
      };
    },
    buildPeriodSelectionOptions(period) {
      const now = dayjs();
      const options = [];
      if (period === 'month') {
        for (let i = 0; i < 24; i += 1) {
          const monthDate = now.startOf('month').subtract(i, 'month');
          options.push(
            this.createRangeOption(
              period,
              monthDate.startOf('month'),
              monthDate.endOf('month'),
              monthDate.format('YYYY年MM月'),
              monthDate.format('YYYY-MM')
            )
          );
        }
        return options;
      }
      if (period === 'quarter') {
        for (let i = 0; i < 12; i += 1) {
          const ref = now.subtract(i * 3, 'month');
          const q = Math.floor(ref.month() / 3) + 1;
          const quarterStartMonth = (q - 1) * 3 + 1;
          const quarterStart = dayjs(`${ref.year()}-${String(quarterStartMonth).padStart(2, '0')}-01`).startOf('day');
          const quarterEnd = quarterStart.add(3, 'month').subtract(1, 'second');
          options.push(
            this.createRangeOption(
              period,
              quarterStart,
              quarterEnd,
              `${quarterStart.format('YYYY')}年Q${q}`,
              `${quarterStart.format('YYYY')}-Q${q}`
            )
          );
        }
        return options;
      }
      if (period === 'half_year') {
        for (let i = 0; i < 8; i += 1) {
          const ref = now.subtract(i * 6, 'month');
          const isFirstHalf = ref.month() < 6;
          const year = ref.year();
          const start = isFirstHalf ? dayjs(`${year}-01-01`) : dayjs(`${year}-07-01`);
          const end = isFirstHalf ? dayjs(`${year}-06-30`).endOf('day') : dayjs(`${year}-12-31`).endOf('day');
          options.push(
            this.createRangeOption(
              period,
              start.startOf('day'),
              end,
              `${year}年${isFirstHalf ? '上半年' : '下半年'}`,
              `${year}-H${isFirstHalf ? 1 : 2}`
            )
          );
        }
        return options;
      }
      if (period === 'year') {
        for (let i = 0; i < 6; i += 1) {
          const yearDate = now.startOf('year').subtract(i, 'year');
          options.push(
            this.createRangeOption(
              period,
              yearDate.startOf('year'),
              yearDate.endOf('year'),
              `${yearDate.format('YYYY')}年`,
              yearDate.format('YYYY')
            )
          );
        }
        return options;
      }
      // week default
      for (let i = 0; i < 20; i += 1) {
        const base = now.subtract(i, 'week');
        const weekStart = this.getStartOfWeek(base);
        const weekEnd = weekStart.add(6, 'day').endOf('day');
        const label = `${weekStart.format('YYYY/MM/DD')}-${weekEnd.format('MM/DD')}`;
        options.push(
          this.createRangeOption(
            period,
            weekStart,
            weekEnd,
            label,
            `W-${weekStart.format('YYYYMMDD')}`
          )
        );
      }
      return options;
    },
    initPeriodSelection() {
      this.periodSelectionOptions = this.buildPeriodSelectionOptions(this.query.period);
      if (this.periodSelectionOptions.length > 1) {
        this.query.currentPeriodKey = this.periodSelectionOptions[0].key;
        this.query.comparePeriodKey = this.periodSelectionOptions[1].key;
      }
    },
    handlePeriodTypeChange() {
      this.initPeriodSelection();
      this.loadBoardData();
    },
    async handleCurrentPeriodChange() {
      const idx = this.periodSelectionOptions.findIndex((item) => item.key === this.query.currentPeriodKey);
      if (idx !== -1 && idx < this.periodSelectionOptions.length - 1) {
        this.query.comparePeriodKey = this.periodSelectionOptions[idx + 1].key;
      } else {
        // 当前周期已经是最后一项时，对比周期置空
        this.query.comparePeriodKey = '';
      }
      await this.loadBoardData();
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  padding: 20px;
  background: #f3f6fb;
  min-height: calc(100vh - 84px);
}

.title {
  margin-bottom: 20px;
}

.box-card {
  margin-bottom: 16px;
  border-radius: 10px;
}

::v-deep .el-card {
  background: #ffffff;
  border: 1px solid #e8edf5;
  box-shadow: 0 6px 20px rgba(31, 45, 61, 0.06);
}

::v-deep .el-breadcrumb__inner {
  font-size: 15px;
  color: #4a5872;
}

::v-deep .el-breadcrumb__item:last-child .el-breadcrumb__inner {
  color: #1f2d3d;
  font-weight: 600;
}

.search-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
  display: flex;
  align-items: center;
}

.search-title i {
  margin-right: 6px;
  color: #2d6cdf;
}

.search-hint {
  margin-top: 8px;
  margin-bottom: 14px;
  color: #6b778c;
  font-size: 13px;
}

.filter-row {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-block {
  display: flex;
  flex-direction: column;
}

.filter-box {
  border: 1px solid #dce6f8;
  background: #f7faff;
  border-radius: 8px;
  padding: 8px 10px;
}

.filter-label {
  font-size: 12px;
  color: #6b778c;
  margin-bottom: 6px;
}

.filter-item {
  width: 200px;
}

.button-block .el-button {
  height: 36px;
}

.alert-highlight {
  margin-left: auto;
  align-self: center;
  min-width: 210px;
  border: 1px solid #ffd6d6;
  background: linear-gradient(135deg, #fff6f6 0%, #ffecec 100%);
  border-radius: 10px;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.alert-highlight:hover {
  box-shadow: 0 8px 18px rgba(245, 108, 108, 0.22);
  transform: translateY(-1px);
}

.alert-title {
  font-size: 13px;
  color: #b71c1c;
  display: flex;
  align-items: center;
  font-weight: 600;
}

.alert-title i {
  margin-right: 6px;
}

.alert-value {
  margin-top: 4px;
  font-size: 32px;
  color: #cf1322;
  font-weight: 700;
  line-height: 1.1;
}

.alert-desc {
  margin-top: 4px;
  color: #8c2a2a;
  font-size: 12px;
}

.period-compare-box {
  border: 1px solid #dce6f8;
  background: #f7faff;
  border-radius: 8px;
  padding: 8px 10px;
}

.period-compare-header {
  font-size: 12px;
  color: #5d6b82;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.current-dot,
.compare-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
}

.current-dot {
  background: #2d6cdf;
}

.compare-dot {
  background: #e67e22;
  margin-left: 12px;
}

.vs-text {
  margin: 0 10px;
  color: #8a97ad;
  font-weight: 600;
}

.period-compare-row {
  display: flex;
  gap: 8px;
}

.period-item {
  width: 190px;
}

.time-range {
  margin-top: 12px;
  color: #4a5872;
  font-size: 13px;
}

.mt-20 {
  margin-top: 20px;
}

.kpi-card {
  text-align: center;
  background: #ffffff;
}

.kpi-card.clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.kpi-card.clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(31, 45, 61, 0.12);
}

.kpi-title {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a5872;
  font-size: 14px;
  font-weight: 600;
}

.kpi-icon {
  margin-right: 6px;
  font-size: 16px;
}

.kpi-value {
  margin-top: 10px;
  font-size: 28px;
  line-height: 1.1;
  font-weight: bold;
  color: #1f2d3d;
}

.kpi-value.compact {
  font-size: 28px;
  white-space: nowrap;
}

.kpi-value.main {
  color: #2d6cdf;
}

.kpi-value.success {
  color: #35a874;
}

.kpi-value.warning {
  color: #d48806;
}

.kpi-value.danger {
  color: #cf1322;
}

.kpi-value.muted {
  color: #596780;
}

.kpi-compare {
  margin-top: 10px;
  min-height: 20px;
  font-size: 13px;
  color: #909399;
}

.up {
  color: #e67e22;
}

.down {
  color: #409eff;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2d3d;
  margin-bottom: 12px;
}

.chart-box {
  height: 320px;
}

.alert-drawer-body {
  padding: 0 12px 12px;
}

.drawer-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 8px;
}

.staff-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
</style>
